/**
 * React Query hooks for transcription data fetching
 * Wraps transcription API with React Query for automatic caching and state management
 */

import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import transcriptionKeys from '@/api/querry_keys/transcriptionKeys';
import { auth } from '@/lib/picasso-firebase';
const API_BASE_URL = import.meta.env.VITE_BASE_URL;

export interface Transcription {
  title: string;
  transcription: string;
  user_email: string;
  created_at: string;
}

/**
 * Hook for fetching transcriptions with infinite scroll pagination
 */
export const useTranscriptionsInfinite = () => {
  return useInfiniteQuery({
    queryKey: transcriptionKeys.user(),
    queryFn: async ({ pageParam = 0 }) => {
      const currentUser = auth.currentUser;
      if (!currentUser) {
        throw new Error("Authentication required");
      }
      const token = await currentUser.getIdToken();

      const response = await fetch(
        `${API_BASE_URL}/picasso/transcription/get-transcriptions?limit=12&offset=${pageParam}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      return await response.json();
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage) => {
      const nextOffset = (lastPage.offset || 0) + (lastPage.transcriptions?.length || 0);
      return lastPage.has_more ? nextOffset : undefined;
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 30, // 30 minutes
  });
};

/**
 * Hook for uploading a transcript file
 */
interface UploadTranscriptParams {
  file: File;
  model: string;
}

export const useUploadTranscript = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ file, model }: UploadTranscriptParams) => {
      const currentUser = auth.currentUser;
      if (!currentUser) {
        throw new Error("Authentication required");
      }
      const token = await currentUser.getIdToken();

      const formData = new FormData();
      formData.append('file', file);
      formData.append('model', model);

      const url = `${API_BASE_URL}/picasso/transcription/upload`;

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          // DO NOT set Content-Type - browser will automatically set it with boundary for FormData
        },
        body: formData,
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Upload failed:', response.status, errorText);
        throw new Error(`Upload failed: ${response.status} ${response.statusText}`);
      }

      return await response.json();
    },
    onSuccess: () => {
      // Invalidate transcription queries to refetch the list
      queryClient.invalidateQueries({ queryKey: transcriptionKeys.user() });
    },
  });
};

/**
 * Hook for fetching a single transcription
 */
export const useTranscription = (transcriptionId: string | null) => {
  return useInfiniteQuery({
    queryKey: transcriptionKeys.detail(transcriptionId || ''),
    queryFn: async () => {
      const currentUser = auth.currentUser;
      if (!currentUser || !transcriptionId) {
        throw new Error("Authentication and transcription ID required");
      }
      const token = await currentUser.getIdToken();

      const response = await fetch(
        `${API_BASE_URL}/picasso/transcription/${transcriptionId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      return await response.json();
    },
    enabled: !!transcriptionId,
    staleTime: 1000 * 60 * 5, // 5 minutes
    initialPageParam: 0,
    getNextPageParam: () => undefined,
  });
};

/**
 * Helper hook that provides transcription data with a compatible interface
 */
export const useTranscriptionsWithMutations = () => {
  const transcriptionsQuery = useTranscriptionsInfinite();

  // Flatten pages into single array
  const transcriptions = transcriptionsQuery.data?.pages.flatMap(page => page.transcriptions) ?? [];

  // Get total counts from the first page
  const totalCount = transcriptionsQuery.data?.pages[0]?.total_count ?? 0;
  const totalUnfilteredCount = transcriptionsQuery.data?.pages[0]?.total_unfiltered_count ?? totalCount;

  return {
    // Transcription data
    transcriptions,

    // Loading states
    isLoading: transcriptionsQuery.isLoading,
    isLoadingMore: transcriptionsQuery.isFetchingNextPage,

    // Error states
    error: transcriptionsQuery.error,

    // Pagination
    hasMore: transcriptionsQuery.hasNextPage ?? false,
    loadMore: transcriptionsQuery.fetchNextPage,

    // Counts
    totalCount,
    totalUnfilteredCount,

    // Refetch functions
    refetch: async () => {
      await transcriptionsQuery.refetch();
    },

    // Raw query objects for advanced use cases
    transcriptionsQuery,
  };
};
